import { useState, useMemo } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { api } from '../lib/api';
import type { User } from '../App';

// Known active promotion codes and the price they yield. The server is the
// source of truth (it re-validates the code against Stripe), so this map
// only exists to show the customer the right price before checkout.
const KNOWN_PROMOS: Record<string, { discountCents: number; label: string }> = {
  QUIZ100: { discountCents: 10000, label: 'Quiz Taker Discount' },
};
const LIST_PRICE_CENTS = 34900;

/**
 * app.constare.academy landing page.
 *
 *  - Signed-in active users skip this page entirely and land straight
 *    on the dashboard. The old "Welcome back / Open dashboard" interstitial
 *    was one wasted click on every visit.
 *
 *  - Signed-out visitors see the enrollment hero and a Stripe-hosted
 *    checkout form. All marketing content lives on
 *    constare.academy - this page is intentionally the last screen
 *    before payment.
 */
export default function LandingPage({ user }: { user: User | null }) {
  const [email, setEmail] = useState(user?.email ?? '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Read the promo code from ?promo=... on the URL. The server re-validates
  // it against Stripe on submit, so we do not trust this value for anything
  // besides display.
  const promoCode = useMemo(() => {
    if (typeof window === 'undefined') return '';
    const raw = new URLSearchParams(window.location.search).get('promo') || '';
    return raw.trim().toUpperCase();
  }, []);
  const promoMeta = promoCode ? KNOWN_PROMOS[promoCode] : undefined;
  const displayCents = promoMeta ? LIST_PRICE_CENTS - promoMeta.discountCents : LIST_PRICE_CENTS;
  const displayPrice = `$${Math.round(displayCents / 100)}`;
  const listPrice = `$${Math.round(LIST_PRICE_CENTS / 100)}`;

  async function startCheckout(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await api.post<{ url: string }>('/api/stripe/checkout', {
        email,
        ...(promoCode ? { promoCode } : {}),
      });
      window.location.href = res.url;
    } catch (err) {
      const code = (err as { error?: string })?.error;
      if (code === 'stripe_not_configured') {
        setError('Payments are not configured yet. Check back shortly.');
      } else if (code === 'invalid_email') {
        setError('Enter a valid email.');
      } else {
        setError('Could not start checkout. Please try again.');
      }
      setLoading(false);
    }
  }

  if (user?.status === 'active') {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="py-8">
      <div className="grid gap-12 md:grid-cols-2 md:gap-16">
        <div>
          <p className="font-mono text-xs uppercase tracking-widest text-amber">
            Constare Academy
          </p>
          <h1 className="mt-3 text-4xl font-semibold leading-tight tracking-tight md:text-5xl">
            The ground keeps moving. Learn how to stand.
          </h1>
          <p className="mt-5 text-lg text-muted">
            An 8-week AI training program for high school and college students who want a real shot at the AI economy, without losing their judgment, their character, or their mind to it.
          </p>
          <ul className="mt-6 space-y-2 text-sm text-muted">
            <li>
              • Weekly cadence. New material unlocks each week from your start
              date.
            </li>
            <li>
              • Skip weeks when travel or life gets in the way. Pick up where
              you left off.
            </li>
            <li>
              • Three threads run from week 1 to week 8: fluency, judgment,
              character.
            </li>
          </ul>
          <p className="mt-6 text-sm text-muted">
            Learn more about the program at{' '}
            <a
              href="https://www.constare.academy/"
              className="text-amber underline underline-offset-2 hover:text-amber-hover"
            >
              constare.academy
            </a>
            .
          </p>
        </div>

        <div className="rounded-2xl border border-line bg-surface p-8">
          <div className="flex items-baseline justify-between">
            <h2 className="text-xl font-semibold">Get Started</h2>
            {promoMeta ? (
              <div className="flex items-baseline gap-2 font-mono">
                <span className="text-lg text-muted line-through">{listPrice}</span>
                <span className="text-2xl text-amber">{displayPrice}</span>
              </div>
            ) : (
              <div className="font-mono text-2xl text-amber">{listPrice}</div>
            )}
          </div>
          <p className="mt-2 text-sm text-muted">
            One time. Full access to the program at your own pace.
          </p>

          {promoMeta && (
            <div className="mt-4 rounded-md border border-amber/40 bg-amber/5 px-3 py-2">
              <p className="text-xs font-mono uppercase tracking-wider text-amber">
                Code {promoCode} applied
              </p>
              <p className="mt-1 text-sm text-text">
                ${Math.round(promoMeta.discountCents / 100)} off. Discount applies at checkout.
              </p>
            </div>
          )}

          <form onSubmit={startCheckout} className="mt-6 space-y-3">
            <label className="block">
              <span className="text-xs uppercase tracking-wider text-muted">
                Student email
              </span>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="student@example.com"
                className="mt-1 w-full rounded-md border border-line bg-ink px-3 py-2 text-sm text-text placeholder:text-muted focus:border-amber focus:outline-none"
              />
              <p className="mt-2 text-xs text-muted">
                Use the student&#39;s email. This is the account that will sign in and take the program.
              </p>
            </label>
            {error && (
              <p className="rounded-md border border-line bg-ink px-3 py-2 text-sm text-amber">
                {error}
              </p>
            )}
            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-md bg-amber px-4 py-2.5 font-semibold text-ink hover:bg-amber-hover disabled:opacity-60"
            >
              {loading ? 'Starting checkout…' : 'Continue to checkout'}
            </button>
          </form>

          <p className="mt-4 text-xs text-muted">
            You will get a sign-in link the moment payment clears. See our{' '}
            <Link
              to="/refunds"
              className="text-amber underline underline-offset-2 hover:text-amber-hover"
            >
              refund policy
            </Link>
            .
          </p>
        </div>
      </div>
    </div>
  );
}
